import React, { Component } from 'react';
import { FormGroup, FormControl, Table } from 'react-bootstrap';
import LoaderButton from '../../components/LoaderButton';
import Button from '../../components/atoms/forms/Button';
import { CreateLocationRoutes, OpsLocations } from '../../libs/GraphQL';
import { uploadFileToS3 } from '../../libs/uploadToS3';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import {
  OPTIONAL_COLUMNS,
  REQUIRED_FIELDS,
  isRowEmpty,
  linesWithInvertedWindow,
  routesMissingShiftToken,
  unknownColumns,
  validateRows,
} from './validation';
import {
  ValidationSummary,
  RowStatusCell,
  ErrorSubRow,
} from './ValidationDisplay';

const StyledRoutesUpload = styledComponent('div', {
  '& tr.wr-row-error td': {
    background: theme.colors.dangerBg,
  },

  '& .wr-line-number': {
    color: theme.colors.textMuted,
  },

  '& .wr-table-actions': {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing.gapSm,
  },
});

const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== '';

export default class ChangeWeeklyRoutes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      fileContents: '',
      file: null,
      locations: [],
      locationsLoading: true,
      locationsError: false,
      collapsedRows: {},
      uploadError: null,
      uploadSuccess: null,
    };
    // Memoization caches — rebuilt only when the underlying state changes.
    this.locationIdCache = { locations: null, ids: null };
    this.validationCache = { data: null, locationIds: null, results: null };
    this.getOpsLocations = this.getOpsLocations.bind(this);
    this.makeChanges = this.makeChanges.bind(this);
    this.toggleRow = this.toggleRow.bind(this);
    this.toggleAllErrorRows = this.toggleAllErrorRows.bind(this);
  }

  componentDidMount() {
    this.getOpsLocations();
  }

  async getOpsLocations() {
    this.setState({ locationsLoading: true, locationsError: false });
    try {
      const response = await OpsLocations({ status: ['LIVE'] });
      const locations = response?.data?.opsLocations?.locations || [];
      this.setState({ locations, locationsLoading: false });
    } catch (e) {
      console.error(e);
      this.setState({ locationsLoading: false, locationsError: true });
    }
  }

  // Set of LIVE location ids (as strings), or null while locations are
  // unavailable — null tells validateRows to skip the location check.
  getLocationIdSet() {
    if (this.state.locationsLoading || this.state.locationsError) {
      return null;
    }
    if (this.locationIdCache.locations !== this.state.locations) {
      this.locationIdCache = {
        locations: this.state.locations,
        ids: new Set(this.state.locations.map((loc) => String(loc.id))),
      };
    }
    return this.locationIdCache.ids;
  }

  // Unknown CSV headers — blocking, since a typo'd column drops its data.
  getUnknownHeaders() {
    if (this.state.fileContents === '') {
      return [];
    }
    return unknownColumns(this.state.fileContents.meta?.fields);
  }

  // Per-row validation results, parallel to fileContents.data. Cached per
  // (data, locationIds) pair so rows re-validate automatically when the
  // locations query resolves, and both render and makeChanges share one pass.
  getRowValidations() {
    if (this.state.fileContents === '') {
      return [];
    }
    const locationIds = this.getLocationIdSet();
    const { data } = this.state.fileContents;
    if (
      this.validationCache.data !== data ||
      this.validationCache.locationIds !== locationIds
    ) {
      this.validationCache = {
        data,
        locationIds,
        results: validateRows(data, locationIds),
      };
    }
    return this.validationCache.results;
  }

  handleFileChange = (event) => {
    const file = event.target.files[0];
    this.setState({
      fileContents: '',
      file,
      collapsedRows: {},
      uploadError: null,
      uploadSuccess: null,
    });
    if (!file) {
      return;
    }
    window.Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      // 'greedy' also drops whitespace-only lines (a trailing newline
      // otherwise becomes a garbage row).
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const data = results.data
          .filter((row) => !isRowEmpty(row))
          .map((row) => ({
            ...row,
            id: row.id === null || row.id === undefined ? '' : String(row.id),
          }));
        this.setState({ fileContents: { ...results, data } });
      },
    });
  };

  toggleRow(index) {
    this.setState((prevState) => ({
      collapsedRows: {
        ...prevState.collapsedRows,
        [index]: !prevState.collapsedRows[index],
      },
    }));
  }

  // Fold every error detail away, or bring them all back.
  toggleAllErrorRows() {
    const validations = this.getRowValidations();
    const errorIndexes = validations
      .map((validation, index) => (validation.status < 0 ? index : null))
      .filter((index) => index !== null);
    this.setState((prevState) => {
      const anyExpanded = errorIndexes.some(
        (index) => !prevState.collapsedRows[index],
      );
      return {
        collapsedRows: anyExpanded
          ? Object.fromEntries(errorIndexes.map((index) => [index, true]))
          : {},
      };
    });
  }

  async makeChanges() {
    const validations = this.getRowValidations();
    // The button is disabled while rows are invalid or locations are
    // unavailable; this guard covers any path around it.
    if (
      this.getLocationIdSet() === null ||
      this.getUnknownHeaders().length > 0 ||
      validations.some((validation) => validation.status < 0)
    ) {
      return;
    }
    this.setState({ isLoading: true, uploadError: null, uploadSuccess: null });

    const rows = this.state.fileContents.data;
    const groups = {};
    rows.forEach((obj) => {
      const key = `${obj.date}|${String(obj.route).toLowerCase()}`;
      if (!groups[key]) {
        groups[key] = { payload: [], rows: [] };
      }
      const routeData = {
        stockingDate: String(obj.date),
        market: String(obj.market),
        id: String(obj.id),
        route: String(obj.route).toLowerCase(),
        stop: Number.parseInt(obj.stop, 10),
        stockingIndex: Number.parseInt(obj.stockingIndex, 10),
        stopType: String(obj.stopType).toUpperCase(),
      };
      if (hasValue(obj.calculatedStartTime)) {
        routeData.calculatedStartTime = obj.calculatedStartTime;
      }
      if (hasValue(obj.calculatedEndTime)) {
        routeData.calculatedEndTime = obj.calculatedEndTime;
      }
      if (hasValue(obj.isStripeStop)) {
        routeData.isStripeStop =
          obj.isStripeStop === true ||
          String(obj.isStripeStop).toLowerCase() === 'true';
      }
      if (hasValue(obj.nextmvRunId)) {
        routeData.nextmvRunId = String(obj.nextmvRunId);
      }
      if (hasValue(obj.expectedDropoffDate)) {
        routeData.expectedDropoffDate = String(obj.expectedDropoffDate);
      }
      groups[key].payload.push(routeData);
      groups[key].rows.push(obj);
    });

    let archiveWarning = '';
    try {
      await uploadFileToS3(this.state.file, 'routes-upload');
    } catch (uploadError) {
      console.error('Error in S3 file upload:', uploadError);
      archiveWarning = ' Note: archiving the CSV to S3 failed.';
    }

    const failedRows = [];
    let failedGroups = 0;
    const groupKeys = Object.keys(groups);
    // Route groups upload sequentially on purpose — no-await-in-loop and
    // no-restricted-syntax are the pagination-style exception.
    // eslint-disable-next-line no-restricted-syntax
    for (const key of groupKeys) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await CreateLocationRoutes(groups[key].payload);
      } catch (e) {
        console.error(e);
        failedGroups += 1;
        failedRows.push(...groups[key].rows);
      }
    }

    if (failedRows.length > 0) {
      this.setState((prevState) => ({
        fileContents: { ...prevState.fileContents, data: failedRows },
        collapsedRows: {},
        isLoading: false,
        uploadError: `${failedRows.length} stop${
          failedRows.length > 1 ? 's' : ''
        } across ${failedGroups} route group${
          failedGroups > 1 ? 's' : ''
        } failed to upload. The failed rows are kept below — retry the upload.${archiveWarning}`,
      }));
    } else {
      this.setState({
        fileContents: '',
        file: null,
        isLoading: false,
        uploadSuccess: `Uploaded ${rows.length} stop${
          rows.length > 1 ? 's' : ''
        } across ${groupKeys.length} route group${
          groupKeys.length > 1 ? 's' : ''
        }.${archiveWarning}`,
      });
    }
  }

  renderSummary(validations) {
    const {
      fileContents,
      locationsLoading,
      locationsError,
      uploadError,
      uploadSuccess,
    } = this.state;

    if (uploadError) {
      return (
        <ValidationSummary tone="danger" title="Upload failed">
          {uploadError}
        </ValidationSummary>
      );
    }
    if (uploadSuccess) {
      return <ValidationSummary tone="success" title={uploadSuccess} />;
    }
    if (locationsError) {
      return (
        <ValidationSummary tone="danger" title="Couldn't load locations">
          Refresh the page to retry. Upload is disabled until location data
          loads.
        </ValidationSummary>
      );
    }
    if (fileContents === '') {
      return null;
    }
    if (fileContents.data.length === 0) {
      return (
        <ValidationSummary
          tone="danger"
          title="No data rows found in this file"
        >
          The CSV parsed, but every row was empty. Check the file and re-upload.
        </ValidationSummary>
      );
    }
    if (locationsLoading) {
      return (
        <ValidationSummary tone="info" title="Checking locations…">
          Upload is disabled until location data finishes loading.
        </ValidationSummary>
      );
    }
    const unknownHeaders = this.getUnknownHeaders();
    if (unknownHeaders.length > 0) {
      return (
        <ValidationSummary
          tone="danger"
          title={`Unrecognized column${
            unknownHeaders.length > 1 ? 's' : ''
          }: ${unknownHeaders.join(', ')}`}
        >
          {`CSV headers must match the expected format. Valid columns: ${[
            ...REQUIRED_FIELDS,
            ...OPTIONAL_COLUMNS,
          ].join(', ')}. Fix the header${
            unknownHeaders.length > 1 ? 's' : ''
          } and re-upload.`}
        </ValidationSummary>
      );
    }
    const total = validations.length;
    const errorCount = validations.filter(
      (validation) => validation.status < 0,
    ).length;
    if (errorCount > 0) {
      return (
        <ValidationSummary
          tone="danger"
          title={`${errorCount} of ${total} row${
            total > 1 ? 's' : ''
          } can't be uploaded`}
        >
          Fix the rows flagged below in your spreadsheet and re-upload the file
          to enable upload.
        </ValidationSummary>
      );
    }
    return (
      <ValidationSummary
        tone="success"
        title={`All ${total} row${
          total > 1 ? 's' : ''
        } valid — ready to upload`}
      />
    );
  }

  // Non-blocking notices: unrecognized headers, route names without a
  // shift token.
  renderNotices() {
    if (this.state.fileContents === '') {
      return null;
    }
    const missingShift = routesMissingShiftToken(this.state.fileContents.data);
    const invertedWindows = linesWithInvertedWindow(
      this.state.fileContents.data,
    );
    return (
      <>
        {invertedWindows.length > 0 && (
          <ValidationSummary
            tone="warning"
            title={`calculatedStartTime is after calculatedEndTime on line${
              invertedWindows.length > 1 ? 's' : ''
            } ${invertedWindows.join(', ')}`}
          >
            Double-check those time windows. Upload is still allowed.
          </ValidationSummary>
        )}
        {missingShift.length > 0 && (
          <ValidationSummary
            tone="warning"
            title={`Route name${
              missingShift.length > 1 ? 's' : ''
            } without an _am_/_pm_ token: ${missingShift.join(', ')}`}
          >
            The Monitoring shift filter can&apos;t classify these routes — they
            will only show under &quot;All&quot;. Upload is still allowed.
          </ValidationSummary>
        )}
      </>
    );
  }

  render() {
    const validations = this.getRowValidations();
    const errorCount = validations.filter(
      (validation) => validation.status < 0,
    ).length;
    const anyErrorExpanded = validations.some(
      (validation, index) =>
        validation.status < 0 && !this.state.collapsedRows[index],
    );

    let headers;
    let rows;
    if (this.state.fileContents !== '') {
      const firstRow = this.state.fileContents.data[0] || {};
      const displayColumns = [
        ...REQUIRED_FIELDS,
        ...OPTIONAL_COLUMNS.filter((col) => col in firstRow),
      ];

      headers = [
        <th key="line-number">line</th>,
        ...displayColumns.map((header) => <th key={header}>{header}</th>),
        <th key="status">status</th>,
      ];

      rows = this.state.fileContents.data.map((obj, index) => {
        const validation = validations[index];
        const hasErrors = validation.status < 0;
        const expanded = hasErrors && !this.state.collapsedRows[index];
        const lineNumber = index + 2;

        return (
          // Row identity is positional — CSV rows have no stable id, and
          // value-derived keys collide on duplicate rows.
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={`line-${index}`}>
            <tr className={hasErrors ? 'wr-row-error' : undefined}>
              <td className="wr-line-number">{lineNumber}</td>
              {displayColumns.map((header) => (
                <td key={header}>
                  {typeof obj[header] === 'boolean'
                    ? String(obj[header])
                    : obj[header]}
                </td>
              ))}
              <td>
                <RowStatusCell
                  errorCount={validation.errors.length}
                  expanded={expanded}
                  onToggle={() => this.toggleRow(index)}
                />
              </td>
            </tr>
            {expanded && (
              <ErrorSubRow
                colSpan={displayColumns.length + 2}
                lineNumber={lineNumber}
                errors={validation.errors}
              />
            )}
          </React.Fragment>
        );
      });
    }

    return (
      <StyledRoutesUpload className="Locations">
        <FormGroup controlId="file">
          <FormControl
            onClick={(event) => {
              // eslint-disable-next-line no-param-reassign
              event.target.value = null;
            }}
            onChange={this.handleFileChange}
            type="file"
          />
        </FormGroup>

        {this.renderSummary(validations)}
        {this.renderNotices()}

        {errorCount > 0 && (
          <div className="wr-table-actions">
            <Button
              variant="link"
              onClick={this.toggleAllErrorRows}
              dataTestId="wr-toggle-all-errors"
            >
              {anyErrorExpanded
                ? 'Collapse all error details'
                : 'Expand all error details'}
            </Button>
          </div>
        )}
        {this.state.fileContents !== '' && (
          <Table bordered responsive>
            <thead>
              <tr>{headers}</tr>
            </thead>
            <tbody>{rows}</tbody>
          </Table>
        )}

        <LoaderButton
          block
          bsStyle="primary"
          bsSize="large"
          disabled={
            this.state.fileContents === '' ||
            this.state.fileContents.data.length === 0 ||
            this.state.locationsLoading ||
            this.state.locationsError ||
            this.getUnknownHeaders().length > 0 ||
            errorCount > 0
          }
          onClick={this.makeChanges}
          type="submit"
          isLoading={this.state.isLoading}
          text="Upload Routes"
          loadingText="Uploading Routes…"
        />
      </StyledRoutesUpload>
    );
  }
}
